import { RadioGroup } from '@headlessui/react';
import { CardBackground } from 'components/ui';
import { isNumber } from 'lodash';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useUpdateOnboardingSettingsMutation } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { channelViaList, companySize, jobTitles } from './descriptions';
import { StepsLayout } from './StepsLayout';
import { useOnboardingState } from './useOnboardingState';

export const ProfileStep = () => {
  const history = useHistory();
  const {
    jobTitle,
    setJobTitle,
    teamSize,
    setTeamSize,
    channelVia,
    setChannelVia,
    localStorageKey,
  } = useOnboardingState();

  const hasUserSelectedOptions =
    isNumber(jobTitle) && isNumber(teamSize) && channelVia.length > 0;
  const { rawQueryParams } = useParseURLQueryParams();
  const queryParams = `store_name=${rawQueryParams?.store_name}`;

  const [updateOnboardingSettings] = useUpdateOnboardingSettingsMutation();

  const handleOnNext = () => {
    const savedProgress = localStorage.getItem(localStorageKey);

    localStorage.setItem(
      localStorageKey,
      JSON.stringify({
        ...(savedProgress ? JSON.parse(savedProgress) : {}),
        jobTitle,
        teamSize,
        channelVia,
      })
    );

    updateOnboardingSettings({
      serializedStoreName: queryParams,
      body: {
        channel_via: channelVia,
        job_title: isNumber(jobTitle) ? jobTitle : null,
        staff_size: isNumber(teamSize) ? teamSize : null,
      },
    });

    history.push({
      ...history.location,
      pathname: '/onboarding/objectives',
    });
  };

  useEffect(() => {
    const currentOnboarding = localStorage.getItem(localStorageKey);

    if (currentOnboarding) {
      const { jobTitle, teamSize, channelVia } = JSON.parse(currentOnboarding);

      if (isNumber(jobTitle)) {
        setJobTitle(jobTitle);
      }
      if (isNumber(teamSize)) {
        setTeamSize(teamSize);
      }
      if (channelVia?.length) {
        setChannelVia(channelVia);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepsLayout>
      <div className="px-2.5 mt-4 lg:grid lg:grid-cols-8 lg:mt-10 lg:gap-x-9 lg:px-0">
        <div className="col-span-3 px-2.5 lg:px-0">
          <p className="text-black text-opacity-30">1/4</p>
          <p className="mt-4 text-2xl font-bold lg:leading-8 lg:text-segments-32px">
            Let's get started
          </p>

          <div className="mt-4">
            <p>Tell us about yourself! We'd like to get to know you better.</p>
          </div>
        </div>
        <div className="col-span-5 mt-10 lg:mt-0 ">
          <CardBackground>
            <div className="px-5 lg:p-10 py-7">
              <div>
                <RadioGroup value={jobTitle} onChange={setJobTitle}>
                  <RadioGroup.Label className="font-bold">
                    I am a
                  </RadioGroup.Label>
                  <div className="flex flex-wrap gap-3 mt-3 lg:gap-0 lg:space-x-3">
                    {jobTitles.map(({ label, value }) => (
                      <RadioGroup.Option value={value} key={value}>
                        {({ checked }) => (
                          <span
                            className={` w-[150px] rounded-3xl h-[68px] flex justify-center items-center px-2 text-center cursor-pointer  shadow-sm
                         ${
                           checked
                             ? 'border-segments-link text-segments-link bg-segments-accent-green-3 border-2 font-bold'
                             : 'bg-segments-gray-F8'
                         }
                        `}
                          >
                            {label}
                          </span>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              <div className="mt-12">
                <RadioGroup value={teamSize} onChange={setTeamSize}>
                  <RadioGroup.Label className="font-bold">
                    My company size is
                  </RadioGroup.Label>
                  <div className="grid grid-cols-2 xl:grid-cols-4 mt-3 gap-3">
                    {companySize.map(({ label, value }) => (
                      <RadioGroup.Option value={value} key={value}>
                        {({ checked }) => (
                          <span
                            className={`w-[150px] rounded-3xl h-[68px] flex justify-center items-center  text-center cursor-pointer shadow-sm 
                         ${
                           checked
                             ? 'border-2 border-segments-link text-segments-link font-bold bg-segments-accent-green-3'
                             : 'bg-segments-gray-F8'
                         }
                        `}
                          >
                            {label}
                          </span>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              <div className="mt-12">
                <RadioGroup
                  value={channelVia[0]}
                  onChange={(value) =>
                    setChannelVia(value !== undefined ? [value] : [])
                  }
                >
                  <RadioGroup.Label className="font-bold">
                    I found Segments through
                  </RadioGroup.Label>
                  <div className="grid grid-cols-2 gap-3 mt-3 xl:grid-cols-3">
                    {channelViaList.map(({ label, value }) => (
                      <RadioGroup.Option value={value} key={value}>
                        {({ checked }) => (
                          <span
                            className={`lg:w-[170px] h-[36px] rounded-3xl flex justify-center items-center  text-center cursor-pointer shadow-sm 
                         ${
                           checked
                             ? 'border-2 border-segments-link text-segments-link font-bold bg-segments-accent-green-3'
                             : 'bg-segments-gray-F8'
                         }
                        `}
                          >
                            {label}
                          </span>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>
            </div>
          </CardBackground>
        </div>
      </div>

      <div className="flex items-center justify-center my-10 space-x-8 ">
        <button
          onClick={handleOnNext}
          className={`px-14 lg:px-20 py-3 font-bold ${
            hasUserSelectedOptions
              ? 'text-white rounded-xl bg-segments-accent-color-1 '
              : 'text-black text-opacity-50'
          } `}
        >
          {hasUserSelectedOptions ? 'Next' : 'Skip'}
        </button>
      </div>
    </StepsLayout>
  );
};
